import React, { useState, useEffect } from 'react';
import { X, MapPin, Users, DollarSign, FileText } from 'lucide-react';

const CommuneFormModal = ({ commune, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    district: '',
    description: '',
    population: '',
    average_price: '',
    amenities: {
      schools: [],
      hospitals: [],
      markets: [],
      transport: [],
      entertainment: []
    }
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (commune) {
      setFormData({
        name: commune?.name || '',
        district: commune?.district || '',
        description: commune?.description || '',
        population: commune?.population?.toString() || '',
        average_price: commune?.average_price?.toString() || '',
        amenities: commune?.amenities || {
          schools: [],
          hospitals: [],
          markets: [],
          transport: [],
          entertainment: []
        }
      });
    }
  }, [commune]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAmenityChange = (category, value) => {
    const amenities = value?.split(',')?.map(item => item?.trim())?.filter(item => item?.length > 0);
    setFormData(prev => ({
      ...prev,
      amenities: {
        ...prev?.amenities,
        [category]: amenities
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Le nom de la commune est requis';
    }

    if (!formData?.district?.trim()) {
      newErrors.district = 'Le district est requis';
    }

    if (formData?.population && isNaN(parseInt(formData?.population))) {
      newErrors.population = 'La population doit être un nombre';
    }

    if (formData?.average_price && isNaN(parseFloat(formData?.average_price))) {
      newErrors.average_price = 'Le prix moyen doit être un nombre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    
    const submitData = {
      ...formData,
      population: formData?.population ? parseInt(formData?.population) : null,
      average_price: formData?.average_price ? parseFloat(formData?.average_price) : null
    };

    const success = await onSave?.(submitData);
    
    if (success) {
      onClose?.();
    }
    
    setSaving(false);
  };

  const districts = [
    'Abidjan',
    'Bouaké',
    'Daloa',
    'Korhogo',
    'San-Pédro',
    'Yamoussoukro',
    'Man',
    'Divo',
    'Gagnoa',
    'Agboville'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-blue-600" />
            {commune ? 'Modifier la commune' : 'Nouvelle commune'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de la commune *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: Cocody"
              />
              {errors?.name && <p className="mt-1 text-sm text-red-600">{errors?.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                District *
              </label>
              <select
                value={formData?.district}
                onChange={(e) => handleInputChange('district', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.district ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner un district</option>
                {districts?.map(district => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
              {errors?.district && <p className="mt-1 text-sm text-red-600">{errors?.district}</p>}
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Users className="h-4 w-4 mr-1" />
                Population
              </label>
              <input
                type="number"
                value={formData?.population}
                onChange={(e) => handleInputChange('population', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.population ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 500000"
              />
              {errors?.population && <p className="mt-1 text-sm text-red-600">{errors?.population}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                Prix moyen (FCFA)
              </label>
              <input
                type="number"
                value={formData?.average_price}
                onChange={(e) => handleInputChange('average_price', e?.target?.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.average_price ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 50000000"
              />
              {errors?.average_price && <p className="mt-1 text-sm text-red-600">{errors?.average_price}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-1" />
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Description de la commune..."
            />
          </div>

          {/* Amenities */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Commodités</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Écoles (séparées par des virgules)
                </label>
                <input
                  type="text"
                  value={formData?.amenities?.schools?.join(', ') || ''}
                  onChange={(e) => handleAmenityChange('schools', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: École Primaire Cocody, Lycée Moderne..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hôpitaux (séparés par des virgules)
                </label>
                <input
                  type="text"
                  value={formData?.amenities?.hospitals?.join(', ') || ''}
                  onChange={(e) => handleAmenityChange('hospitals', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: CHU de Cocody, Clinique Notre Dame..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marchés (séparés par des virgules)
                </label>
                <input
                  type="text"
                  value={formData?.amenities?.markets?.join(', ') || ''}
                  onChange={(e) => handleAmenityChange('markets', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Marché de Cocody, Centre Commercial..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transport (séparé par des virgules)
                </label>
                <input
                  type="text"
                  value={formData?.amenities?.transport?.join(', ') || ''}
                  onChange={(e) => handleAmenityChange('transport', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Gare routière, Station de bus..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Divertissement (séparé par des virgules)
                </label>
                <input
                  type="text"
                  value={formData?.amenities?.entertainment?.join(', ') || ''}
                  onChange={(e) => handleAmenityChange('entertainment', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Cinéma, Parc, Centre culturel..."
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Sauvegarde...' : commune ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommuneFormModal;